<template>
  <div id="app">
    <header class="header">
      <div class="header-container glass-surface">
        <router-link to="/" class="logo-link" @click="closeMenu">
          <img src="/paulie-wordmark-black.png" alt="Paulie" class="logo-image" />
        </router-link>

        <button class="menu-toggle" @click="menuOpen = !menuOpen" :aria-expanded="menuOpen" aria-label="Toggle navigation">
          ☰
        </button>

        <nav class="nav" :class="{ open: menuOpen }">
          <router-link to="/" class="nav-link" @click="closeMenu">Home</router-link>
          <router-link to="/data" class="nav-link" @click="closeMenu">Info</router-link>
          <router-link to="/quick-stats" class="nav-link" :class="{ 'nav-link--active': isDataSection }" @click="closeMenu">Data</router-link>
          <router-link to="/pricing" class="nav-link" @click="closeMenu">Pricing</router-link>
          <router-link to="/pro-dashboard" class="nav-link" @click="closeMenu">Dashboard</router-link>
          <button v-if="isAuthenticated" type="button" class="nav-sign-out nav-download-slot" @click="signOut">Sign Out</button>
          <a v-else href="/#download" class="nav-download" @click="closeMenu">Download</a>
          <router-link v-if="influencerEnabled" :to="isAuthenticated ? '/dashboard' : '/influencers'" class="nav-link" @click="closeMenu">
            {{ isAuthenticated ? 'Dashboard' : 'Influencers' }}
          </router-link>
        </nav>
      </div>
    </header>

    <main class="main-content">
      <router-view />
    </main>

    <footer class="footer">
      <div class="footer-container glass-surface">
        <div class="footer-content">
          <div class="footer-section">
            <h4>Legal</h4>
            <ul>
              <li><a href="/privacy-policy.pdf" target="_blank" rel="noopener noreferrer">Privacy Policy</a></li>
              <li><a href="/terms-of-service.pdf" target="_blank" rel="noopener noreferrer">Terms of Service</a></li>
            </ul>
          </div>
          <div class="footer-section">
            <h4>Support</h4>
            <ul>
              <li><a href="mailto:support@paulie.chat">Ask for Help</a></li>
              <li><router-link to="/pricing">Pricing</router-link></li>
              <li><router-link to="/quick-stats">Data</router-link></li>
              <li><router-link to="/pro-dashboard">Dashboard</router-link></li>
            </ul>
          </div>
          <div v-if="influencerEnabled" class="footer-section">
            <h4>Influencers</h4>
            <ul>
              <li><router-link to="/influencers">Info</router-link></li>
              <li v-if="!isAuthenticated"><router-link to="/login" class="shiny-login-btn">Login</router-link></li>
              <li v-else><button @click="signOut" class="shiny-login-btn sign-out-btn">Sign Out</button></li>
            </ul>
          </div>
        </div>
        <div class="footer-bottom"><p>&copy; 2025 Paulie. All rights reserved.</p></div>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const menuOpen = ref(false)

const isAuthenticated = computed(() => authStore.isAuthenticated)
const isDataSection = computed(() => route.path.startsWith('/quick-stats'))
const influencerEnabled = import.meta.env.VITE_FEATURE_INFLUENCER === 'true'

const closeMenu = () => { menuOpen.value = false }

const signOut = async () => {
  try {
    await authStore.signOut()
    closeMenu()
    router.push('/')
  } catch (error) {
    console.error('Sign out error:', error)
  }
}

onMounted(() => {
  authStore.initializeAuth()
})
</script>

<style scoped>
#app { min-height: 100vh; display: flex; flex-direction: column; }
.glass-surface { background: transparent; border: 0; box-shadow: none; }
.header { position: fixed; inset: 0 0 auto 0; z-index: 1000; height: 84px; padding: .75rem 1rem; background: rgba(255, 255, 255, 0.78); backdrop-filter: blur(18px); -webkit-backdrop-filter: blur(18px); }
.header-container { max-width: 1200px; margin: 0 auto; border-radius: 18px; min-height: 100%; padding: .35rem 1rem; display:flex; align-items:center; justify-content:space-between; gap: 1rem; position:relative; }
.logo-link { display:flex; align-items:center; }
.logo-image { height: 34px; width:auto; }
.menu-toggle { display:none; min-width:44px; min-height:44px; border-radius:10px; border:0; background: #f4f4ef; color: var(--text-primary); font-size:1.25rem; }
.nav { display:flex; gap:.35rem; }
.nav-link { color:var(--text-secondary); text-decoration:none; font-weight:500; font-size:.92rem; border-radius:10px; border:1px solid transparent; padding:.46rem .82rem; }
.nav-link.router-link-active,
.nav-link--active { color:#fff; background: var(--accent); border-color: transparent; }
.nav-sign-out { border:1px solid rgba(248,113,113,.72); border-radius:8px; background: linear-gradient(180deg, rgba(239,68,68,.96), rgba(185,28,28,.96)); color:#fff; cursor:pointer; font:inherit; font-size:.88rem; font-weight:800; padding:.42rem .9rem; box-shadow: 0 8px 18px rgba(127,29,29,.22), inset 0 1px 0 rgba(255,255,255,.18); transition: filter .2s ease, transform .2s ease; }
.nav-sign-out:hover { filter: brightness(1.08); transform: translateY(-1px); }
.nav-download-slot { min-height: 34px; }
.nav-download { color:#fff; text-decoration:none; font-weight:700; font-size:.88rem; border-radius:8px; padding:.42rem .9rem; background:var(--accent); border:0; transition: background .2s ease, opacity .2s ease; }
.nav-download:hover { background:#3654ff; }
.main-content { flex:1; margin-top:84px; }
.footer { padding: .75rem 1rem 1.2rem; border-top: 1px solid var(--border); }
.footer-container { max-width:1200px; margin:0 auto; border-radius:16px; padding:1rem 1.2rem .8rem; }
.footer-content { display:grid; grid-template-columns: repeat(auto-fit,minmax(140px,1fr)); gap:1rem; }
.footer-section h4 { font-size:.9rem; margin-bottom:.35rem; }
.footer-section ul { list-style:none; display:flex; gap:.85rem; flex-wrap:wrap; }
.footer-section a { color:var(--text-secondary); text-decoration:none; font-size:.82rem; }
.shiny-login-btn { display:inline-flex; align-items:center; justify-content:center; border-radius:10px; border:1px solid rgba(247,216,123,.52); background: linear-gradient(115deg, rgba(255,243,198,.9) 0%, rgba(244,203,98,.95) 30%, rgba(227,170,61,.92) 65%, rgba(255,241,185,.9) 100%); color:#3f2a02 !important; padding:.35rem .8rem; font-weight:700; font-size:.82rem; }
.sign-out-btn { background: linear-gradient(135deg, rgba(220,38,38,.84), rgba(248,113,113,.8)) !important; border-color: rgba(248,113,113,.45) !important; color: #fff !important; }
.footer-bottom { text-align:center; margin-top:.75rem; padding-top:.7rem; border-top:0; }
.footer-bottom p { color:var(--text-muted); font-size:.74rem; }

@media (max-width: 768px) {
  .header { height: 72px; padding: .45rem .65rem; }
  .main-content { margin-top: 72px; }
  .logo-image { height: 30px; }
  .menu-toggle { display:inline-flex; align-items:center; justify-content:center; }
  .nav { position:absolute; top: calc(100% + .45rem); left: 0; right:0; display:none; flex-direction:column; padding:.65rem; border-radius:14px; background: #ffffff; border:0; box-shadow: var(--shadow-sm); }
  .nav.open { display:flex; }
  .nav-link,
  .nav-sign-out { min-height:44px; display:flex; align-items:center; }
  .footer { padding: .4rem .65rem 1rem; }
  .footer-content { grid-template-columns: 1fr; text-align:center; }
  .footer-section ul { justify-content:center; }
}
</style>
